/**
 * Cognito Client - Auth API Version
 * Replaces direct Cognito SDK calls with Auth API REST calls
 * Maintains same interface for backward compatibility
 */

import {
  CognitoIdentityProviderClient,
  GetUserCommand,
  GlobalSignOutCommand,
  InitiateAuthCommand
} from '@aws-sdk/client-cognito-identity-provider';
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { getTenantConfig, getAuthApiUrl } from '../../config/tenantConfig';

/**
 * Get Cognito Identity Provider client (for operations that still need SDK)
 */
const getClient = () => {
  const config = getTenantConfig();
  return new CognitoIdentityProviderClient({
    region: config.cognito.region
  });
};

/**
 * Get Cognito configuration from tenant config
 */
const getCognitoConfig = () => {
  return getTenantConfig().cognito;
};

/**
 * Sign in with Account ID, email, and password
 * @param {string} accountId - 12-digit Account ID
 * @param {string} email - User's email
 * @param {string} password - User's password
 * @returns {Promise<Object>} Authentication result or challenge
 */
export const signInUser = async (accountId, email, password) => {
  const authApiUrl = getAuthApiUrl();

  const response = await fetch(`${authApiUrl}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      accountId,
      email,
      password
    })
  });

  if (!response.ok) {
    const error = await response.json();
    const apiError = new Error(error.error || 'Authentication failed');
    apiError.name = response.status === 401 ? 'NotAuthorizedException' :
                    response.status === 429 ? 'TooManyRequestsException' :
                    'UnknownError';
    throw apiError;
  }

  const data = await response.json();

  // Transform to Cognito SDK format for compatibility
  if (data.challengeName) {
    return {
      ChallengeName: data.challengeName,
      Session: data.session,
      ChallengeParameters: data.challengeParameters
    };
  }

  return {
    AuthenticationResult: {
      IdToken: data.idToken,
      AccessToken: data.accessToken,
      RefreshToken: data.refreshToken,
      ExpiresIn: data.expiresIn || 3600
    }
  };
};

/**
 * Sign up new user
 * @param {Object} signupData - Signup data
 * @param {string} signupData.email - Email
 * @param {string} signupData.password - Password
 * @param {string} signupData.handle - Display name
 * @param {string} signupData.selectedTenantId - Tenant ID
 * @returns {Promise<Object>}
 */
export const signUpUser = async ({ email, password, handle, selectedTenantId }) => {
  const authApiUrl = getAuthApiUrl();

  const response = await fetch(`${authApiUrl}/auth/signup`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      email,
      password,
      handle,
      selectedTenantId
    })
  });

  if (!response.ok) {
    const error = await response.json();
    const apiError = new Error(error.error || 'Signup failed');
    apiError.name = 'SignupException';
    throw apiError;
  }

  const data = await response.json();

  // Return data including Account ID
  return {
    success: data.success,
    accountId: data.accountId,
    userId: data.userId,
    emailSent: data.emailSent,
    message: data.message,
    fallbackMode: data.fallbackMode
  };
};

/**
 * Get available tenants for signup
 * @returns {Promise<Array>} List of tenants
 */
export const getAvailableTenants = async () => {
  const authApiUrl = getAuthApiUrl();

  const response = await fetch(`${authApiUrl}/auth/tenants`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error('Failed to fetch available tenants');
  }

  return await response.json();
};

/**
 * Initiate forgot password flow
 * @param {string} accountId - 12-digit Account ID
 * @param {string} email - User's email
 */
export const forgotUserPassword = async (accountId, email) => {
  const authApiUrl = getAuthApiUrl();

  const response = await fetch(`${authApiUrl}/auth/forgot-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      accountId,
      email
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Password reset request failed');
  }

  return await response.json();
};

/**
 * Confirm forgot password with code
 * @param {string} accountId - 12-digit Account ID
 * @param {string} code - Verification code
 * @param {string} newPassword - New password
 */
export const confirmForgotUserPassword = async (accountId, code, newPassword) => {
  const authApiUrl = getAuthApiUrl();

  const response = await fetch(`${authApiUrl}/auth/reset-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      accountId,
      code,
      newPassword
    })
  });

  if (!response.ok) {
    const error = await response.json();
    const apiError = new Error(error.error || 'Password reset failed');
    apiError.name = error.error?.includes('code') ? 'CodeMismatchException' :
                    error.error?.includes('expired') ? 'ExpiredCodeException' :
                    error.error?.includes('password') ? 'InvalidPasswordException' :
                    'UnknownError';
    throw apiError;
  }

  return await response.json();
};

/**
 * Get current user details
 * @param {string} accessToken
 * @returns {Promise<Object>}
 */
export const getCurrentUser = async (accessToken) => {
  const client = getClient();

  const command = new GetUserCommand({
    AccessToken: accessToken
  });

  return await client.send(command);
};

/**
 * Global sign out
 * @param {string} accessToken
 */
export const globalSignOut = async (accessToken) => {
  const client = getClient();

  const command = new GlobalSignOutCommand({
    AccessToken: accessToken
  });

  return await client.send(command);
};

/**
 * Refresh user tokens using refresh token
 * @param {string} refreshToken - Refresh token
 * @returns {Promise<Object>} New authentication tokens
 */
export const refreshUserTokens = async (refreshToken) => {
  const client = getClient();
  const config = getCognitoConfig();

  const command = new InitiateAuthCommand({
    AuthFlow: 'REFRESH_TOKEN_AUTH',
    ClientId: config.clientId,
    AuthParameters: {
      REFRESH_TOKEN: refreshToken
    }
  });

  return await client.send(command);
};

/**
 * Respond to MFA challenge
 * NOTE: MFA challenges still use Cognito SDK (not exposed via Auth API)
 * @param {string} session - Challenge session from signIn
 * @param {string} challengeName - Challenge type (SMS_MFA, SOFTWARE_TOKEN_MFA)
 * @param {string} mfaCode - MFA code from user
 * @returns {Promise<Object>} Authentication result
 */
export const respondToMfaChallenge = async (session, challengeName, mfaCode) => {
  const client = getClient();
  const config = getCognitoConfig();

  const { RespondToAuthChallengeCommand } = await import('@aws-sdk/client-cognito-identity-provider');

  const command = new RespondToAuthChallengeCommand({
    ClientId: config.clientId,
    ChallengeName: challengeName,
    Session: session,
    ChallengeResponses: {
      [challengeName === 'SMS_MFA' ? 'SMS_MFA_CODE' : 'SOFTWARE_TOKEN_MFA_CODE']: mfaCode
    }
  });

  return await client.send(command);
};

/**
 * Respond to NEW_PASSWORD_REQUIRED challenge via Auth API
 * @param {string} session - Challenge session from signIn
 * @param {string} newPassword - New password
 * @param {string} accountId - Account ID
 * @returns {Promise<Object>} Authentication result
 */
export const respondToNewPasswordChallenge = async (session, newPassword, accountId) => {
  const authApiUrl = getAuthApiUrl();

  const response = await fetch(`${authApiUrl}/auth/complete-new-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      accountId,
      newPassword,
      session
    })
  });

  if (!response.ok) {
    const error = await response.json();
    const apiError = new Error(error.error || 'Failed to set new password');
    apiError.name = error.error?.includes('password') ? 'InvalidPasswordException' :
                    error.error?.includes('session') ? 'NotAuthorizedException' :
                    'UnknownError';
    throw apiError;
  }

  const data = await response.json();

  return {
    AuthenticationResult: {
      IdToken: data.IdToken,
      AccessToken: data.AccessToken,
      RefreshToken: data.RefreshToken,
      ExpiresIn: data.ExpiresIn
    }
  };
};

/**
 * Get AWS credentials from Cognito Identity Pool
 * @param {string} idToken
 * @returns {Promise<Object>} AWS credentials
 */
export const getAWSCredentials = async (idToken) => {
  const config = getTenantConfig();
  const identityPoolId = config.cognito.identityPoolId;

  const credentials = fromCognitoIdentityPool({
    client: new CognitoIdentityClient({ region: config.cognito.region }),
    identityPoolId,
    logins: {
      [`cognito-idp.${config.cognito.region}.amazonaws.com/${config.cognito.userPoolId}`]: idToken
    }
  });

  return credentials;
};

// DEPRECATED - Keep for backward compatibility but not used
export const confirmSignUpUser = async () => {
  throw new Error('Email verification no longer required - accounts are auto-verified');
};
